import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AcademicHolidayService } from 'src/app/services/api/academic-holidays/academic-holiday.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { AcademicHolidayModel, AcademicHolidayPayLoad } from 'src/app/services/models/academic-holidays/academic_holidays.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { ErrorMessage } from 'src/utils/error-message';
import { getSafeErrorText } from 'src/utils/http-error-message';
import { SetError } from 'src/utils/set-error.validator';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

export type AcademicHolidaySaveAction = 'create' | 'edit';

export interface AcademicHolidaySaveFailedEvent {
  action: AcademicHolidaySaveAction;
  message: string;
}

@Component({
  selector: 'app-form-add-academic-holiday',
  imports: [CommonModule, TranslateModule, SharedModule, ReactiveFormsModule],
  templateUrl: './form-add-academic-holiday.component.html',
  styleUrl: './form-add-academic-holiday.component.scss',
})
export class FormAddAcademicHolidayComponent implements AfterViewInit {
  @ViewChild('saveAcademicHolidayConfirmDialog')
  saveAcademicHolidayConfirmDialog!: ConfirmDialogComponent;

  @Output() saved = new EventEmitter<AcademicHolidaySaveAction>();
  @Output() saveFailed = new EventEmitter<AcademicHolidaySaveFailedEvent>();
  @Output() closed = new EventEmitter<void>();

  fb = inject(FormBuilder);
  cdr = inject(ChangeDetectorRef);
  academicHolidayService = inject(AcademicHolidayService);
  studyPeriodService = inject(StudyPeriodService);

  isSaving = false;
  academicTerms: AcademicTermYearModel[] = [];
  yearOptions: string[] = [];
  termOptions: AcademicTermYearModel[] = [];
  academicHolidayId?: number;
  errors: Partial<Record<string, string>> = {};

  form = this.fb.group({
    academicTermId: [null as number | null, Validators.required],
    year: [null as string | null, Validators.required],
    name: ['', Validators.required],
    holidayDate: [null as string | null, Validators.required],
  });

  private isPatchingForm = false;

  constructor() {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.valueChanges.subscribe(() => {
        this.clearResolvedError(key);
      });
    });
  }

  get isEditMode(): boolean {
    return this.academicHolidayId != null;
  }

  ngAfterViewInit(): void {
    const modalEl = document.getElementById('addNewAcademicHoliday');

    modalEl?.addEventListener('hidden.bs.modal', () => {
      this.clearForm();
      this.closed.emit();
    });
  }

  openModal(academicHolidayId?: number): void {
    this.clearForm();
    this.academicHolidayId = academicHolidayId;

    if (academicHolidayId == null) {
      this.loadAcademicTermYears();
    } else {
      this.loadAcademicHolidayDetail(academicHolidayId);
    }

    const modalEl = document.getElementById('addNewAcademicHoliday');

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  onYearChange(year: string | null): void {
    this.termOptions = this.academicTerms.filter(
      (term) => String(this.toBuddhistYear(term.year)) === year,
    );

    if (this.isPatchingForm) {
      return;
    }

    this.form.patchValue({ academicTermId: null });
  }

  onSave(): void {
    this.errors = {};

    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errors = this.mapErrorsToMessages(SetError(this.form));
      return;
    }

    this.saveAcademicHolidayConfirmDialog.open();
  }

  onConfirmSave(): void {
    this.errors = {};

    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errors = this.mapErrorsToMessages(SetError(this.form));
      this.saveAcademicHolidayConfirmDialog.close();
      return;
    }

    this.isSaving = true;
    const action = this.getSaveAction();

    this.getSaveRequest(this.buildPayload()).subscribe({
      next: () => {
        this.saveAcademicHolidayConfirmDialog.close();
        this.saved.emit(action);
        this.closeModal();
      },
      error: (err: any) => {
        const serverErrors = err.error?.errors ?? err.error ?? {};

        this.errors = this.mapErrorsToMessages(this.mapServerErrors(serverErrors));
        this.saveAcademicHolidayConfirmDialog.close();
        this.saveFailed.emit({
          action,
          message: this.getSaveErrorMessage(err, action),
        });
        this.cdr.detectChanges();
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      },
    });
  }

  clearForm(): void {
    this.academicHolidayId = undefined;
    this.form.reset({
      academicTermId: null,
      year: null,
      name: '',
      holidayDate: null,
    });
    this.termOptions = [];
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.errors = {};
  }

  private loadAcademicTermYears(): void {
    this.studyPeriodService.getAcademicTermYears().subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.yearOptions = Array.from(
          new Set(terms.map((term) => String(this.toBuddhistYear(term.year)))),
        ).sort((a, b) => a.localeCompare(b));
      },
      error: (err) => console.error(err),
    });
  }

  private loadAcademicHolidayDetail(academicHolidayId: number): void {
    this.academicHolidayService.getAcademicHolidayById(academicHolidayId).subscribe({
      next: (holiday) => this.patchFormValue(holiday),
      error: (err) => console.error(err),
    });
  }

  private patchFormValue(holiday: AcademicHolidayModel): void {
    const academicYear = holiday.year ?? holiday.academic_term?.year;
    const year = academicYear == null ? '' : String(this.toBuddhistYear(academicYear));
    const academicTermId = holiday.academic_term?.id ?? null;

    this.academicTerms = this.toAcademicTermOptions(holiday);
    this.yearOptions = year === '' ? [] : [year];
    this.termOptions = this.academicTerms;
    this.isPatchingForm = true;
    this.form.patchValue({
      academicTermId,
      year,
      name: holiday.name,
      holidayDate: holiday.holiday_date ?? holiday.start_date ?? null,
    });
    this.isPatchingForm = false;
  }

  private toAcademicTermOptions(holiday: AcademicHolidayModel): AcademicTermYearModel[] {
    if (holiday.academic_term == null) {
      return [];
    }

    return [{
      id: holiday.academic_term.id,
      name: holiday.academic_term.name,
      year: holiday.academic_term.year,
    }];
  }

  private getSaveRequest(payload: AcademicHolidayPayLoad) {
    if (this.academicHolidayId == null) {
      return this.academicHolidayService.createAcademicHoliday(payload);
    }

    return this.academicHolidayService.updateAcademicHoliday(this.academicHolidayId, payload);
  }

  private getSaveAction(): AcademicHolidaySaveAction {
    return this.academicHolidayId == null ? 'create' : 'edit';
  }

  private buildPayload(): AcademicHolidayPayLoad {
    const value = this.form.getRawValue();
    const holidayDate = this.formatDate(value.holidayDate);

    return {
      academic_holiday: {
        academic_term_id: Number(value.academicTermId),
        name: value.name ?? '',
        year: this.toChristianYear(value.year),
        holiday_date: holidayDate,
      },
    };
  }

  private formatDate(date: any): string {
    if (date == null || date === '') {
      return '';
    }

    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    if (typeof date === 'string') {
      return new Date(date).toISOString().split('T')[0];
    }

    if ('year' in date && 'month' in date && 'day' in date) {
      const month = String(date.month).padStart(2, '0');
      const day = String(date.day).padStart(2, '0');

      return `${date.year}-${month}-${day}`;
    }

    return '';
  }

  private closeModal(): void {
    const modalElement = document.getElementById('addNewAcademicHoliday');

    if (modalElement == null) {
      return;
    }

    const modal = bootstrap.Modal.getInstance(modalElement);
    modal?.hide();
  }

  private mapServerErrors(errors: unknown): Record<string, unknown> {
    const keyMap: Partial<Record<string, string>> = {
      academic_term: 'academicTermId',
      academic_term_id: 'academicTermId',
      name: 'name',
      year: 'year',
      holiday_date: 'holidayDate',
    };

    if (Array.isArray(errors)) {
      return errors.reduce<Record<string, unknown>>((result, error: unknown) => {
        const { key, message } = this.parseServerErrorText(error);
        this.appendServerError(result, key, message);

        return result;
      }, {} as Record<string, unknown>);
    }

    if (typeof errors === 'string') {
      const { key, message } = this.parseServerErrorText(errors);

      return { [key]: message };
    }

    if (typeof errors !== 'object' || errors == null) {
      return {};
    }

    return Object.entries(errors).reduce((result, [key, value]) => {
      const normalizedKey = this.normalizeServerErrorKey(key);
      const mappedKey = keyMap[normalizedKey] ?? key;

      this.appendServerError(result, mappedKey, value);

      return result;
    }, {} as Record<string, unknown>);
  }

  private appendServerError(result: Record<string, unknown>, key: string, value: unknown): void {
    if (result[key] == null) {
      result[key] = value;

      return;
    }

    const existingMessages = Array.isArray(result[key])
      ? result[key] as unknown[]
      : [result[key]];
    const newMessages = Array.isArray(value) ? value : [value];

    result[key] = [...existingMessages, ...newMessages];
  }

  private parseServerErrorText(error: unknown): { key: string; message: unknown } {
    if (typeof error !== 'string') {
      return { key: 'holidayDate', message: error };
    }

    const fieldPrefixes: Record<string, string> = {
      academic_term: 'academicTermId',
      academic_term_id: 'academicTermId',
      name: 'name',
      year: 'year',
      holiday_date: 'holidayDate',
    };
    const normalizedError = this.normalizeServerErrorKey(error);
    const matchedPrefix = Object.keys(fieldPrefixes).find(
      (prefix) => normalizedError === prefix || normalizedError.startsWith(`${prefix}_`),
    );

    if (matchedPrefix == null) {
      return { key: 'holidayDate', message: error };
    }

    const displayPrefix = matchedPrefix.replace(/_/g, ' ');
    const message = error
      .replace(new RegExp(`^${displayPrefix}\\s*[:：-]?\\s*`, 'i'), '')
      .trim();

    return {
      key: fieldPrefixes[matchedPrefix],
      message: message === '' ? error : message,
    };
  }

  private normalizeServerErrorKey(key: string): string {
    return key.trim().toLowerCase().replace(/[\s-]+/g, '_');
  }

  private mapErrorsToMessages(errors: Record<string, unknown>): Record<string, string> {
    const errorMessage = new ErrorMessage();

    return Object.keys(errors).reduce((result, key) => {
      result[key] = errorMessage.getErrorMessage(errors[key]);

      return result;
    }, {} as Record<string, string>);
  }

  private clearResolvedError(key: string): void {
    const control = this.form.get(key);

    if (control?.valid === true && this.errors[key] != null) {
      delete this.errors[key];
    }
  }

  private toChristianYear(value: unknown): number {
    const year = Number(value);

    return year > 2400 ? year - 543 : year;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private getSaveErrorMessage(error: unknown, action: AcademicHolidaySaveAction): string {
    const fallbackMessage = action === 'create'
      ? 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง'
      : 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .map((item) => this.getErrorText(item))
          .filter((item) => item !== '');

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return getSafeErrorText(value);
  }
}
